import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import InputAdornment from '@mui/material/InputAdornment'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DownloadIcon from '@mui/icons-material/Download'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import BlockIcon from '@mui/icons-material/Block'
import SendIcon from '@mui/icons-material/Send'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import RefreshIcon from '@mui/icons-material/Refresh'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { DateTime } from 'luxon'
import { decodeFileContentForDisplay, downloadFile } from '../../helpers'
import { FC, invalidateQuery } from '../../Services'

const PAN_REGEX = /\d{14,16}/g

const stateLabels = {
  delivered: 'Consegnato',
  sent: 'Inviato',
  processed: 'Processato',
  error: 'Errore',
  duplicato: 'Duplicato',
  scartato: 'Scartato'
}

const stateColorMap = {
  delivered: 'success',
  sent: 'secondary',
  processed: 'primary',
  error: 'error',
  duplicato: 'info',
  scartato: 'default'
}

const REPROCESS_STATUSES = ['error', 'processed', 'sent']
const NON_REPROCESS_STATUSES = ['duplicato', 'scartato']

const isRowReprocessable = (row) => {
  if (!row || NON_REPROCESS_STATUSES.includes(row.status)) return false
  return (
    REPROCESS_STATUSES.includes(row.status) ||
    !!row?.retryErrors?.length ||
    !!row?.ftpError
  )
}

const docTypes = ['AMA', 'MIR', 'SBR', 'NTV', 'PIC']

const isValidLuhn = (number) => {
  let sum = 0
  let shouldDouble = false

  for (let i = number.length - 1; i >= 0; i--) {
    let digit = parseInt(number[i], 10)
    if (Number.isNaN(digit)) return false

    if (shouldDouble) {
      digit *= 2
      if (digit > 9) digit -= 9
    }

    sum += digit
    shouldDouble = !shouldDouble
  }

  return sum % 10 === 0
}

const getPreview = (content, maxLines = 10000) => {
  if (!content) return ''
  const decoded = decodeFileContentForDisplay(content)
  let part = decoded.split('\n');

  if (maxLines !== -1) {
    part = part.slice(0, maxLines)
  }
  return part.join('\n');
}

const formatFileName = (fileName) => {
  if (!fileName) return ''
  if (fileName.length <= 25) return fileName
  const start = fileName.slice(0, 12)
  const end = fileName.slice(-12)
  return `${start}...${end}`
}

const highlightSearch = (content, search, matchCounter, currentMatchIndex) => {
  if (!search || !content) return content
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escaped})`, 'gi')
  const parts = content.split(regex)
  if (parts.length === 1) return content
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const idx = matchCounter.current++
      const isCurrent = idx === currentMatchIndex
      return (
        <mark
          key={`s-${i}`}
          data-search-match={idx}
          style={{
            backgroundColor: isCurrent ? '#fab387' : '#f9e2af',
            color: '#1e1e2e',
            borderRadius: '2px',
            padding: '0 1px',
            outline: isCurrent ? '2px solid #fab387' : undefined
          }}
        >
          {part}
        </mark>
      )
    }
    return part
  })
}

const applyHighlights = (line, search, matchCounter, currentMatchIndex) => {
  const panHighlighted = highlightPAN(line)
  if (!search) return panHighlighted

  if (typeof panHighlighted === 'string') {
    return highlightSearch(panHighlighted, search, matchCounter, currentMatchIndex)
  }

  return panHighlighted.map((part, i) => {
    if (typeof part === 'string') {
      return <React.Fragment key={`h-${i}`}>{highlightSearch(part, search, matchCounter, currentMatchIndex)}</React.Fragment>
    }
    return part
  })
}

const renderPreviewLines = (content, search, currentMatchIndex) => {
  if (!content) return null
  const lines = content.split('\n')
  const gutterWidth = String(lines.length).length
  const hasSearch = search && search.length > 0
  const searchLower = hasSearch ? search.toLowerCase() : ''
  const matchCounter = { current: 0 }
  return lines.map((line, i) => {
    const isMatch = hasSearch && line.toLowerCase().includes(searchLower)
    return (
      <div key={i} style={{ display: 'flex', backgroundColor: isMatch ? 'rgba(249,226,175,0.1)' : undefined }}>
        <span style={{
          display: 'inline-block',
          minWidth: `${gutterWidth + 1}ch`,
          paddingRight: '1ch',
          textAlign: 'right',
          color: isMatch ? '#f9e2af' : '#6c7086',
          userSelect: 'none',
          borderRight: '1px solid #45475a',
          marginRight: '1ch',
          flexShrink: 0
        }}>
          {i + 1}
        </span>
        <span style={{ flex: 1 }}>{applyHighlights(line, search, matchCounter, currentMatchIndex)}</span>
      </div>
    )
  })
}

const highlightPAN = (text) => {
  if (!text) return text

  const parts = text.split(PAN_REGEX)
  const matches = text.match(PAN_REGEX)

  if (!matches) return text

  return parts.reduce((acc, part, index) => {
    acc.push(part)

    const value = matches[index]
    if (value) {
      const isToken = value.startsWith('99')
      const isValidPan = isValidLuhn(value) && !value.startsWith('0039')

      if (!isToken && !isValidPan) {
        acc.push(value)
        return acc
      }

      acc.push(
        <span
          key={`pan-${index}`}
          title={isToken ? 'Token CDC' : 'PAN valido (Luhn)'}
          style={{
            backgroundColor: isToken ? '#4CAF50' : '#FF9800',
            color: '#000',
            textDecoration: 'underline',
            fontWeight: 600,
            padding: '0 2px',
            borderRadius: '2px'
          }}
        >
          {value}
        </span>
      )
    }

    return acc
  }, [])
}

export const ProcessesTable = ({
  processes,
  totalRows,
  paginationModel,
  onPaginationModelChange,
  docTypeFilter,
  onDocTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  fileNameFilter,
  onFileNameFilterChange,
  hashFilter,
  onHashFilterChange,
  agencies,
  clients,
  isAdmin,
  openModal,
  selectedRows,
  setSelectedRows,
  onRefresh,
  onDeleteProcess
}) => {
  const [previewContent, setPreviewContent] = useState(null)
  const [previewFileName, setPreviewFileName] = useState('')
  const [selectedRowData, setSelectedRowData] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewTab, setPreviewTab] = useState(0)
  const [previewRowId, setPreviewRowId] = useState(null)
  const [previewHashContent, setPreviewHashContent] = useState(null)
  const [previewHashFullContent, setPreviewHashFullContent] = useState(null)
  const [previewHashLoading, setPreviewHashLoading] = useState(false)
  const [previewSearch, setPreviewSearch] = useState('')
  const [searchIndex, setSearchIndex] = useState(0)
  const previewPreRef = useRef(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [downloadTarget, setDownloadTarget] = useState(null)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [discardTarget, setDiscardTarget] = useState(null)
  const [discardLoading, setDiscardLoading] = useState(false)
  const [spedisciTarget, setSpedisciTarget] = useState(null)
  const [spedisciLoading, setSpedisciLoading] = useState(false)

  const fetchProcessDownload = async (id, mode = 'original') => {
    const result = await FC.service('process-download').get(id, { query: { mode } })
    return result
  }

  const handlePreview = async (rowData) => {
    setPreviewFileName(rowData.fileName)
    setPreviewLoading(true)
    setPreviewContent(null)
    setSelectedRowData(null)
    setPreviewTab(0)
    setPreviewRowId(rowData._id)
    setPreviewHashContent(null)
    setPreviewHashFullContent(null)
    try {
      const result = await fetchProcessDownload(rowData._id, 'original')
      if (!result.fileContent) {
        setPreviewContent('Nessun contenuto disponibile per questo processo.')
        setSelectedRowData('')
        return
      }
      setPreviewContent(getPreview(result.fileContent))
      setSelectedRowData(getPreview(result.fileContent, -1))
    } catch (err) {
      window.growl?.show({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile caricare l\'anteprima del file'
      })
    } finally {
      setPreviewLoading(false)
    }
  }

  const fetchHashPreview = async (rowId) => {
    if (!rowId) return
    setPreviewHashLoading(true)
    try {
      const result = await FC.service('process-download').get(rowId, {
        query: { mode: 'sent' }
      })
      if (!result.fileContent) {
        setPreviewHashContent('Nessun contenuto hash disponibile.')
        setPreviewHashFullContent('')
        return
      }
      setPreviewHashContent(getPreview(result.fileContent))
      setPreviewHashFullContent(getPreview(result.fileContent, -1))
    } catch (err) {
      setPreviewHashContent('Errore nel caricamento della versione con hash.')
      setPreviewHashFullContent('')
    } finally {
      setPreviewHashLoading(false)
    }
  }

  const handlePreviewTabChange = (_, newTab) => {
    setPreviewTab(newTab)
    if (newTab === 1 && previewHashContent === null && !previewHashLoading) {
      fetchHashPreview(previewRowId)
    }
  }

  const handleDownloadChoice = async (mode) => {
    if (!downloadTarget || downloadLoading) return
    setDownloadLoading(true)
    try {
      const result = await fetchProcessDownload(downloadTarget._id, mode === 'original' ? 'original' : 'sent')
      await downloadFile(result.fileName, result.fileContent, result.isEncrypted)
      setDownloadTarget(null)
    } catch (err) {
      window.growl?.show({
        severity: 'error',
        summary: 'Errore',
        detail: err?.message || 'Impossibile scaricare il file'
      })
    } finally {
      setDownloadLoading(false)
    }
  }

  const handleDiscardConfirm = async () => {
    if (!discardTarget || discardLoading) return
    setDiscardLoading(true)
    try {
      await FC.service('processes').patch(discardTarget._id, { status: 'scartato' })
      window.growl?.show({
        severity: 'success',
        summary: 'Processo scartato',
        detail: discardTarget.fileName
      })
      setDiscardTarget(null)
      invalidateQuery(['processesPage', 'processesErrorCount'])
      onRefresh?.()
    } catch (err) {
      window.growl?.show({
        severity: 'error',
        summary: 'Errore',
        detail: err?.message || 'Impossibile scartare il processo'
      })
    } finally {
      setDiscardLoading(false)
    }
  }

  const handleSpedisciConfirm = async () => {
    if (!spedisciTarget || spedisciLoading) return
    setSpedisciLoading(true)
    try {
      // Il record duplicato non salva il serverId: va recuperato dall'agenzia, come fa il reprocess.
      const agency = (agencies || []).find(a =>
        a.agencyId === spedisciTarget.agencyId || a._id === spedisciTarget.agencyId
      )
      if (!agency) throw new Error(`Agenzia non trovata per ${spedisciTarget.fileName || spedisciTarget._id}`)
      // Senza serverId il server non puo' tokenizzare: il record finirebbe in 'error'
      // perdendo lo stato 'duplicato' e la possibilita' di rispedirlo.
      if (!agency.serverId) throw new Error(`Nessun server configurato per l'agenzia ${agency.name || agency._id}`)

      const patched = await FC.service('processes').patch(spedisciTarget._id, {
        ...spedisciTarget,
        serverId: agency.serverId,
        reprocess: true,
        spedisci: true
      })

      // La tokenizzazione fallita non fa fallire la patch: lo stato diventa 'error'
      if (patched?.status === 'error') {
        window.growl?.show({
          severity: 'warning',
          summary: 'Spedizione non completata',
          detail: `${spedisciTarget.fileName}: tokenizzazione fallita, il processo è in errore e può essere riprocessato.`
        })
      } else {
        window.growl?.show({
          severity: 'success',
          summary: 'Processo spedito',
          detail: spedisciTarget.fileName
        })
      }
      setSpedisciTarget(null)
      invalidateQuery(['processesPage', 'processesErrorCount'])
      onRefresh?.()
    } catch (err) {
      window.growl?.show({
        severity: 'error',
        summary: 'Errore',
        detail: err?.message || 'Impossibile spedire il processo'
      })
    } finally {
      setSpedisciLoading(false)
    }
  }

  const onPreviewClose = () => {
    setPreviewContent(null)
    setSelectedRowData(null)
    setPreviewFileName('')
    setPreviewLoading(false)
    setPreviewTab(0)
    setPreviewRowId(null)
    setPreviewHashContent(null)
    setPreviewHashFullContent(null)
    setPreviewHashLoading(false)
    setPreviewSearch('')
    setSearchIndex(0)
  }

  const activePreviewText = previewTab === 0 ? previewContent : previewHashContent

  const searchMatchCount = useMemo(() => {
    if (!activePreviewText || !previewSearch) return 0
    const escaped = previewSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return (activePreviewText.match(new RegExp(escaped, 'gi')) || []).length
  }, [activePreviewText, previewSearch])

  const handleSearchChange = useCallback((e) => {
    setPreviewSearch(e.target.value)
    setSearchIndex(0)
  }, [])

  const goToNextMatch = useCallback(() => {
    if (searchMatchCount === 0) return
    setSearchIndex((prev) => (prev + 1) % searchMatchCount)
  }, [searchMatchCount])

  const goToPrevMatch = useCallback(() => {
    if (searchMatchCount === 0) return
    setSearchIndex((prev) => (prev - 1 + searchMatchCount) % searchMatchCount)
  }, [searchMatchCount])

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (e.shiftKey) goToPrevMatch()
      else goToNextMatch()
    }
  }, [goToNextMatch, goToPrevMatch])

  useEffect(() => {
    if (searchMatchCount === 0 || !previewPreRef.current) return
    const el = previewPreRef.current.querySelector(`[data-search-match="${searchIndex}"]`)
    if (el) el.scrollIntoView({ block: 'center', behavior: 'instant' })
  }, [searchIndex, searchMatchCount, previewTab])

  const handleCopyPreview = () => {
    const text = previewTab === 0 ? selectedRowData : previewHashFullContent
    if (text) navigator.clipboard.writeText(text)
  }

  const handleDeleteClick = (row) => {
    setDeleteTarget(row)
    setDeleteConfirmText('')
  }

  const handleDeleteClose = () => {
    setDeleteTarget(null)
    setDeleteConfirmText('')
    setDeleteLoading(false)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleteConfirmText !== deleteTarget.fileName) return
    setDeleteLoading(true)
    try {
      await onDeleteProcess(deleteTarget._id)
      window.growl?.show({
        severity: 'success',
        summary: 'Processo eliminato',
        detail: `Il processo "${deleteTarget.fileName}" è stato eliminato.`
      })
      handleDeleteClose()
    } catch (err) {
      window.growl?.show({
        severity: 'error',
        summary: 'Errore',
        detail: err?.message || 'Impossibile eliminare il processo'
      })
      setDeleteLoading(false)
    }
  }

  const hasActiveFilters = docTypeFilter || statusFilter || fileNameFilter || hashFilter

  const handleClearFilters = () => {
    onDocTypeFilterChange('')
    onStatusFilterChange('')
    onFileNameFilterChange('')
    onHashFilterChange('')
  }

  const agencyMap = useMemo(() => {
    const map = {}
    agencies?.forEach(a => { map[a._id] = a.name || a.agencyId || a._id })
    return map
  }, [agencies])

  const clientMap = useMemo(() => {
    const map = {}
    clients?.forEach(c => { map[c._id] = c.name || c.clientId || c._id })
    return map
  }, [clients])

  const columns = [
    ...(isAdmin
      ? [{
        field: 'agencyId',
        headerName: 'Agenzia',
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Typography variant='body2' fontWeight={500} noWrap>
            {agencyMap[params.value] || params.value}
          </Typography>
        )
      }]
      : []),
    {
      field: 'clientId',
      headerName: 'Client',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight={500} noWrap>
          {clientMap[params.value] || params.value}
        </Typography>
      )
    },
    {
      field: 'docType',
      headerName: 'Tipo',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size='small'
          variant='outlined'
          sx={{ fontWeight: 600, fontSize: 12 }}
        />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Data Creazione',
      width: 170,
      sortable: true,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight={500}>
          {params.value
            ? DateTime.fromISO(params.value).toFormat('dd/MM/yyyy HH:mm')
            : 'N/A'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Stato',
      width: 165,
      renderCell: (params) => {
        const color = stateColorMap[params.value] || 'default'
        const isSuccess = color === 'success'
        // Lo stato 'duplicato' e' gia' esplicito nel chip: l'indicatore serve per i duplicati
        // che hanno attraversato la pipeline (processato / inviato / consegnato).
        const showDuplicateMark = !!params.row?.isDuplicate && params.value !== 'duplicato'
        return (
          <Stack direction='row' alignItems='center' spacing={0.5}>
            <Chip
              label={stateLabels[params.value] || 'N/A'}
              color={color}
              size='small'
              sx={{
                fontWeight: 600,
                fontSize: 12,
                minWidth: 90,
                ...(isSuccess && { color: 'white', '& .MuiChip-label': { color: 'white' } })
              }}
            />
            {showDuplicateMark && (
              <Tooltip title='Contenuto duplicato: identico a un file già processato'>
                <ContentCopyIcon fontSize='small' color='info' aria-label='Contenuto duplicato' />
              </Tooltip>
            )}
          </Stack>
        )
      }
    },
    {
      field: 'fileName',
      headerName: 'Nome File',
      width: 220,
      renderCell: (params) => (
        <Tooltip title={params.value || ''} placement='top'>
          <Typography
            variant='body2'
            fontWeight={500}
            noWrap
            sx={{ maxWidth: 200, cursor: 'default' }}
          >
            {formatFileName(params.value)}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: onDeleteProcess ? 180 : 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const hasErrors = !!params.row?.retryErrors?.length || !!params.row?.ftpError
        const canDiscard = params.row?.status === 'sent'
        // Spedire un duplicato manda un file reale in FTP: riservato a Super Admin e Developer
        const canSpedisci = isAdmin && params.row?.status === 'duplicato'
        return (
          <Stack direction='row' spacing={0.5} alignItems='center'>
            <Tooltip title='Anteprima'>
              <IconButton
                size='small'
                onClick={() => handlePreview(params.row)}
                aria-label={`Anteprima file ${params.row.fileName}`}
                color='primary'
              >
                <VisibilityIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Scarica'>
              <IconButton
                size='small'
                onClick={() => setDownloadTarget(params.row)}
                aria-label={`Scarica file ${params.row.fileName}`}
                color='primary'
              >
                <DownloadIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title={hasErrors ? 'Visualizza errori' : 'Nessun errore'}>
              <span>
                <IconButton
                  size='small'
                  onClick={() => openModal(params.row)}
                  disabled={!hasErrors}
                  aria-label={`Visualizza errori per ${params.row.fileName}`}
                  color='error'
                >
                  <ErrorOutlineIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            {canDiscard && (
              <Tooltip title='Scarta processo'>
                <IconButton
                  size='small'
                  onClick={() => setDiscardTarget(params.row)}
                  aria-label={`Scarta processo ${params.row.fileName}`}
                  color='secondary'
                >
                  <BlockIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
            {canSpedisci && (
              <Tooltip title='Spedisci processo duplicato'>
                <IconButton
                  size='small'
                  onClick={() => setSpedisciTarget(params.row)}
                  aria-label={`Spedisci processo duplicato ${params.row.fileName}`}
                  color='info'
                >
                  <SendIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
            {onDeleteProcess && (
              <Tooltip title='Elimina processo'>
                <IconButton
                  size='small'
                  onClick={() => handleDeleteClick(params.row)}
                  aria-label={`Elimina processo ${params.row.fileName}`}
                  color='error'
                >
                  <DeleteForeverIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )
      }
    }
  ]

  return (
    <>
      {/* Table toolbar filters */}
      <Stack
        direction='row'
        alignItems='center'
        flexWrap='wrap'
        justifyContent='space-between'
        sx={{ gap: 1.5, mb: 1.5 }}
      >
        <Stack direction='row' alignItems='center' flexWrap='wrap' sx={{ gap: 1.5 }}>
          <FilterListIcon fontSize='small' color='action' />
          <Typography variant='body2' fontWeight={600} color='text.secondary'>
            Filtri tabella
          </Typography>

          <FormControl size='small' sx={{ minWidth: 110 }}>
            <InputLabel id='doctype-filter-label'>Tipo Doc</InputLabel>
            <Select
              labelId='doctype-filter-label'
              value={docTypeFilter}
              onChange={(e) => onDocTypeFilterChange(e.target.value)}
              label='Tipo Doc'
            >
              <MenuItem value=''>
                <em>Tutti</em>
              </MenuItem>
              {docTypes.map(dt => <MenuItem key={dt} value={dt}>{dt}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: 130 }}>
            <InputLabel id='status-filter-label'>Stato</InputLabel>
            <Select
              labelId='status-filter-label'
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              label='Stato'
            >
              <MenuItem value=''>
                <em>Tutti</em>
              </MenuItem>
              {Object.entries(stateLabels).map(([key, label]) => {
                const chipColor = stateColorMap[key]
                const isSuccess = chipColor === 'success'
                return (
                  <MenuItem key={key} value={key}>
                    <Chip
                      label={label}
                      color={chipColor}
                      size='small'
                      sx={{
                        fontSize: 11,
                        ...(isSuccess && { color: 'white', '& .MuiChip-label': { color: 'white' } })
                      }}
                    />
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>

          <TextField
            size='small'
            value={fileNameFilter}
            onChange={(e) => onFileNameFilterChange(e.target.value)}
            placeholder='Nome file...'
            sx={{ width: 300 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                )
              }
            }}
          />

          <TextField
            size='small'
            value={hashFilter}
            onChange={(e) => onHashFilterChange(e.target.value)}
            placeholder='Hash...'
            sx={{ width: 260 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                )
              }
            }}
          />

          {hasActiveFilters && (
            <Chip
              label='Cancella filtri'
              size='small'
              variant='outlined'
              onDelete={handleClearFilters}
              onClick={handleClearFilters}
              sx={{ ml: 0.5 }}
            />
          )}
        </Stack>

        {onRefresh && (
          <Tooltip title='Aggiorna dati'>
            <Button
              variant='outlined'
              size='small'
              onClick={onRefresh}
              startIcon={<RefreshIcon />}
              aria-label='Aggiorna tabella processi'
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Aggiorna
            </Button>
          </Tooltip>
        )}
      </Stack>

      {/* DataGrid */}
      <Box sx={{ width: '100%' }}>
        <DataGrid
          rows={processes}
          columns={columns}
          getRowId={(row) => row._id}
          paginationMode='server'
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          rowSelectionModel={{ type: 'include', ids: new Set(selectedRows?.map(r => r._id) || []) }}
          onRowSelectionModelChange={(newModel) => {
            const ids = [...(newModel?.ids || [])]
            const selected = (processes || []).filter(p => ids.includes(p._id))
            setSelectedRows(selected)
          }}
          isRowSelectable={(params) => isRowReprocessable(params.row)}
          disableColumnMenu
          localeText={{
            noRowsLabel: 'Nessun processo trovato.',
            MuiTablePagination: {
              labelRowsPerPage: 'Righe per pagina:',
              labelDisplayedRows: ({ from, to, count }) => `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
            }
          }}
          getRowClassName={(params) =>
            (params.row.retryErrors?.length || params.row.ftpError) ? 'row-has-errors' : ''
          }
          sx={dataGridSx}
        />
      </Box>

      {/* Download choice dialog */}
      <Dialog
        open={!!downloadTarget}
        onClose={() => !downloadLoading && setDownloadTarget(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Scarica file</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {downloadTarget?.fileName}
          </Typography>
          <Stack spacing={1}>
            <Button
              variant='outlined'
              fullWidth
              disabled={downloadLoading}
              onClick={() => handleDownloadChoice('original')}
              startIcon={downloadLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
              sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
            >
              Download senza hash
            </Button>
            <Button
              variant='contained'
              fullWidth
              disabled={downloadLoading}
              onClick={() => handleDownloadChoice('sent')}
              startIcon={downloadLoading ? <CircularProgress size={16} color='inherit' /> : <DownloadIcon />}
              sx={{ textTransform: 'none', justifyContent: 'flex-start' }}
            >
              Download con hash
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size='small'
            variant='outlined'
            onClick={() => setDownloadTarget(null)}
            disabled={downloadLoading}
            sx={{ textTransform: 'none' }}
          >
            Annulla
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discard confirmation dialog */}
      <Dialog
        open={!!discardTarget}
        onClose={() => !discardLoading && setDiscardTarget(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BlockIcon color='secondary' />
          <Typography variant='h6' component='span' fontWeight={600}>
            Scarta processo
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 1 }}>
            Confermi di voler contrassegnare come <strong>scartato</strong> il processo:
          </Typography>
          <Typography variant='body1' fontWeight={700} sx={{ wordBreak: 'break-all' }}>
            {discardTarget?.fileName}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Lo stato passerà da &quot;Inviato&quot; a &quot;Scartato&quot;. L&apos;operazione non è reversibile dalla dashboard.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size='small'
            variant='outlined'
            onClick={() => setDiscardTarget(null)}
            disabled={discardLoading}
            sx={{ textTransform: 'none' }}
          >
            Annulla
          </Button>
          <Button
            size='small'
            variant='contained'
            color='secondary'
            onClick={handleDiscardConfirm}
            disabled={discardLoading}
            startIcon={discardLoading ? <CircularProgress size={16} color='inherit' /> : <BlockIcon />}
            sx={{ textTransform: 'none' }}
          >
            Scarta
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spedisci duplicate confirmation dialog */}
      <Dialog
        open={!!spedisciTarget}
        onClose={() => !spedisciLoading && setSpedisciTarget(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon color='info' />
          <Typography variant='h6' component='span' fontWeight={600}>
            Spedisci processo duplicato
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 1 }}>
            Confermi di voler spedire il processo duplicato:
          </Typography>
          <Typography variant='body1' fontWeight={700} sx={{ wordBreak: 'break-all' }}>
            {spedisciTarget?.fileName}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Il file verrà tokenizzato e inviato via FTP come un processo normale. Resterà
            contrassegnato come duplicato.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size='small'
            variant='outlined'
            onClick={() => setSpedisciTarget(null)}
            disabled={spedisciLoading}
            sx={{ textTransform: 'none' }}
          >
            Annulla
          </Button>
          <Button
            size='small'
            variant='contained'
            color='info'
            onClick={handleSpedisciConfirm}
            disabled={spedisciLoading}
            startIcon={spedisciLoading ? <CircularProgress size={16} color='inherit' /> : <SendIcon />}
            sx={{ textTransform: 'none' }}
          >
            Spedisci
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteClose} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteForeverIcon color='error' />
          <Typography variant='h6' component='span' fontWeight={600}>
            Elimina processo
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 1 }}>
            Stai per eliminare definitivamente il processo:
          </Typography>
          <Typography variant='body1' fontWeight={700} sx={{ mb: 2, wordBreak: 'break-all' }}>
            {deleteTarget?.fileName}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Per confermare, digita il nome del file nel campo sottostante.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size='small'
            label='Nome file'
            placeholder={deleteTarget?.fileName}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleDeleteConfirm()
            }}
            error={deleteConfirmText.length > 0 && deleteConfirmText !== deleteTarget?.fileName}
            helperText={
              deleteConfirmText.length > 0 && deleteConfirmText !== deleteTarget?.fileName
                ? 'Il nome file non corrisponde'
                : ' '
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size='small'
            variant='outlined'
            onClick={handleDeleteClose}
            disabled={deleteLoading}
            sx={{ textTransform: 'none' }}
          >
            Annulla
          </Button>
          <Button
            size='small'
            variant='contained'
            color='error'
            onClick={handleDeleteConfirm}
            disabled={deleteConfirmText !== deleteTarget?.fileName || deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color='inherit' /> : <DeleteForeverIcon />}
            sx={{ textTransform: 'none' }}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview dialog */}
      <Dialog
        open={!!previewFileName}
        onClose={onPreviewClose}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <VisibilityIcon color='primary' />
            <Typography variant='h6' component='span' fontWeight={600}>
              Anteprima
            </Typography>
            <Chip label={previewFileName} size='small' variant='outlined' />
          </Stack>
          <IconButton onClick={onPreviewClose} size='small' aria-label='Chiudi anteprima'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
            <Tabs
              value={previewTab}
              onChange={handlePreviewTabChange}
              sx={{ minHeight: 40 }}
            >
              <Tab label='Senza Hash' sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40 }} />
              <Tab label='Con Hash' sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40 }} />
            </Tabs>
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <TextField
                size='small'
                value={previewSearch}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                placeholder='Cerca nel file...'
                sx={{ width: 220 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon fontSize='small' color='action' />
                      </InputAdornment>
                    ),
                    endAdornment: previewSearch
                      ? (
                        <InputAdornment position='end'>
                          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                            {searchMatchCount > 0 ? `${searchIndex + 1}/${searchMatchCount}` : '0/0'}
                          </Typography>
                        </InputAdornment>
                        )
                      : null
                  }
                }}
              />
              {previewSearch && (
                <>
                  <IconButton size='small' onClick={goToPrevMatch} disabled={searchMatchCount === 0} aria-label='Match precedente'>
                    <KeyboardArrowUpIcon fontSize='small' />
                  </IconButton>
                  <IconButton size='small' onClick={goToNextMatch} disabled={searchMatchCount === 0} aria-label='Match successivo'>
                    <KeyboardArrowDownIcon fontSize='small' />
                  </IconButton>
                </>
              )}
            </Stack>
          </Stack>
          {previewTab === 0 && (
            previewLoading
              ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} />
                </Box>
                )
              : (
                <pre ref={previewPreRef} style={previewPreStyle}>
                  {renderPreviewLines(previewContent, previewSearch, searchIndex)}
                </pre>
                )
          )}
          {previewTab === 1 && (
            previewHashLoading
              ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={32} />
                </Box>
                )
              : (
                <pre ref={previewPreRef} style={previewPreStyle}>
                  {renderPreviewLines(previewHashContent, previewSearch, searchIndex)}
                </pre>
                )
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Tooltip title='Copia contenuto'>
            <Button
              size='small'
              variant='outlined'
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyPreview}
              sx={{ textTransform: 'none' }}
            >
              Copia
            </Button>
          </Tooltip>
          <Button
            size='small'
            variant='outlined'
            onClick={onPreviewClose}
            sx={{ textTransform: 'none' }}
          >
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const previewPreStyle = {
  maxHeight: '60vh',
  overflow: 'auto',
  whiteSpace: 'pre',
  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  fontSize: '12px',
  lineHeight: '1.5',
  background: '#1e1e2e',
  color: '#cdd6f4',
  padding: '1.2rem',
  borderRadius: '8px',
  margin: '0.5rem 0'
}

const dataGridSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  '--DataGrid-containerBackground': '#004680',
  '& .MuiDataGrid-columnHeader': {
    color: '#fff',
    '&:focus, &:focus-within': { outline: 'none' }
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: 13,
    color: '#fff'
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    color: '#fff'
  },
  '& .MuiDataGrid-sortIcon': {
    color: '#fff'
  },
  '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
    color: '#fff'
  },
  '& .MuiDataGrid-columnHeader .MuiCheckbox-root': {
    color: 'rgba(255,255,255,0.7)',
    '&.Mui-checked': {
      color: '#fff'
    }
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center',
    '&:focus, &:focus-within': { outline: 'none' }
  },
  '& .MuiDataGrid-row': {
    '&:hover': {
      backgroundColor: 'action.hover'
    },
    '&.row-has-errors': {
      backgroundColor: 'rgba(244, 67, 54, 0.04)',
      '&:hover': {
        backgroundColor: 'rgba(244, 67, 54, 0.08)'
      }
    }
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderTopColor: 'divider',
    backgroundColor: 'transparent'
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none'
  },
  '& .MuiDataGrid-selectedRowCount': {
    fontSize: 13
  }
}
